import { fromLonLat, toLonLat } from 'ol/proj'
import type { LayerInfo } from './capabilities'

/**
 * Rich deep links (PRD P1.6): `?layer=` names in the URL pre-select advertised
 * layers. Matching is strictly against what the filtered GetCapabilities
 * advertises — unknown names are silently ignored, and the parameter is NEVER
 * an access path: the proxy enforces layer access regardless of what the URL
 * asks for. The query string is left untouched (in-page login keeps it, and
 * the resulting URL stays shareable).
 */
export function requestedLayerNames(search: string): string[] {
  return new URLSearchParams(search)
    .getAll('layer')
    .flatMap((value) => value.split(','))
    .map((value) => value.trim())
    .filter((value) => value !== '')
}

/** Advertised layers matched by the request — by bare or workspace-qualified name. */
export function matchDeepLinkLayers(infos: LayerInfo[], requested: string[]): LayerInfo[] {
  if (requested.length === 0) return []
  const want = new Set(requested)
  return infos.filter((info) => want.has(info.name) || want.has(`${info.workspace}:${info.name}`))
}

/**
 * Build a shareable URL encoding the current map position and visible layers.
 * center is in EPSG:3857 (OpenLayers native). The resulting URL uses the same
 * ?layer= param as incoming deep links so the receiver lands on the same view.
 */
export function buildShareUrl(center: number[], zoom: number, visibleLayers: LayerInfo[]): string {
  const [lon, lat] = toLonLat(center)
  const params = new URLSearchParams()
  params.set('lat', lat.toFixed(5))
  params.set('lon', lon.toFixed(5))
  params.set('zoom', zoom.toFixed(2))
  for (const info of visibleLayers) params.append('layer', info.name)
  return `${location.origin}${location.pathname}?${params.toString()}`
}

/**
 * Read lat/lon/zoom from URL search params. Returns null if any value is
 * absent or unparseable — callers fall back to saved state or default fit.
 */
export function requestedView(search: string): { center: number[]; zoom: number } | null {
  const p = new URLSearchParams(search)
  const lat = parseFloat(p.get('lat') ?? '')
  const lon = parseFloat(p.get('lon') ?? '')
  const zoom = parseFloat(p.get('zoom') ?? '')
  if (isNaN(lat) || isNaN(lon) || isNaN(zoom)) return null
  return { center: fromLonLat([lon, lat]), zoom }
}
